// /reports/accounts-history — long-format export of frozen monthly_snapshot
// rows (one row per account × period). Form: from/to + optional filters.
// Preview shows the first 50 rows; Export pulls everything.
//
// Designed for Excel pivots: drag `period` to Columns, `display_name` (or
// `frt_customer_code`) to Rows, `balance_amount` to Values.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { customers, reports, testers } from '@/lib/api';
import { formatBalance } from '@/lib/format';

function defaultRange(): { from: string; to: string } {
  // 12-month rolling window ending current period.
  const d = new Date();
  const yyyy = d.getUTCFullYear();
  const mm = d.getUTCMonth() + 1;
  const fromY = mm === 12 ? yyyy : yyyy - 1;
  const fromM = mm === 12 ? 1 : mm + 1;
  return {
    from: `${fromY}-${String(fromM).padStart(2, '0')}`,
    to: `${yyyy}-${String(mm).padStart(2, '0')}`,
  };
}

export function AccountsHistoryPage() {
  const initial = defaultRange();
  const [from, setFrom] = useState<string>(initial.from);
  const [to, setTo] = useState<string>(initial.to);
  const [customerId, setCustomerId] = useState<number | ''>('');
  const [testerId, setTesterId] = useState<number | ''>('');
  const [accountStatus, setAccountStatus] = useState<string>('');

  const customerList = useQuery({
    queryKey: ['customers', { per_page: 500 }],
    queryFn: () => customers.list({ per_page: 500 }),
  });
  const testerList = useQuery({
    queryKey: ['testers', { per_page: 500 }],
    queryFn: () => testers.list({ per_page: 500 }),
  });

  const query = {
    from,
    to,
    ...(customerId !== '' ? { frt_customer_id: customerId } : {}),
    ...(testerId !== '' ? { tester_id: testerId } : {}),
    ...(accountStatus ? { account_status: accountStatus } : {}),
  };

  const preview = useQuery({
    queryKey: ['accounts-history', query],
    queryFn: () => reports.accountsHistory(query),
    enabled: Boolean(from) && Boolean(to),
  });

  const onExport = async (format: 'xlsx' | 'csv') => {
    try {
      await reports.accountsHistoryExport(query, format);
    } catch (err) {
      console.error(err);
    }
  };

  const rows = preview.data?.rows ?? [];
  const total = rows.length;
  const previewRows = rows.slice(0, 50);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-semibold text-slate-900">
          Accounts × Period (Historical)
        </h1>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Button size="sm" variant="secondary" onClick={() => onExport('xlsx')}>
            Export XLSX
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onExport('csv')}>
            Export CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardBody className="grid grid-cols-1 sm:grid-cols-5 gap-2 text-sm">
          <label className="block">
            <span className="block text-slate-600">From</span>
            <input
              type="month"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-full rounded border border-slate-300 px-2 py-1.5"
            />
          </label>
          <label className="block">
            <span className="block text-slate-600">To</span>
            <input
              type="month"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-full rounded border border-slate-300 px-2 py-1.5"
            />
          </label>
          <label className="block">
            <span className="block text-slate-600">Customer</span>
            <select
              className="w-full rounded border border-slate-300 bg-white px-2 py-1.5"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">All</option>
              {(customerList.data?.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-slate-600">Tester</span>
            <select
              className="w-full rounded border border-slate-300 bg-white px-2 py-1.5"
              value={testerId}
              onChange={(e) => setTesterId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">All</option>
              {(testerList.data?.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.short_name ?? t.short_business_name ?? `tester#${t.id}`}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-slate-600">Account status</span>
            <select
              className="w-full rounded border border-slate-300 bg-white px-2 py-1.5"
              value={accountStatus}
              onChange={(e) => setAccountStatus(e.target.value)}
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="suppressed">Suppressed</option>
              <option value="closed">Closed</option>
              <option value="converted_personal">Converted personal</option>
            </select>
          </label>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            {preview.isLoading
              ? 'Loading…'
              : `${total} row${total === 1 ? '' : 's'} in range`}
          </CardTitle>
        </CardHeader>
        <CardBody className="overflow-x-auto">
          {preview.error && (
            <div className="text-sm text-red-600 mb-2">
              {(preview.error as Error).message}
            </div>
          )}
          {!preview.isLoading && total === 0 && (
            <div className="text-sm text-slate-500">
              No balance entries fall within this period range.
            </div>
          )}
          {previewRows.length > 0 && (
            <>
              <p className="text-xs text-slate-500 mb-2">
                Preview (first 50 rows). The Export button pulls all {total} rows.
              </p>
              <table className="w-full text-xs">
                <thead className="text-left text-slate-500">
                  <tr>
                    <th className="py-1 pr-2">Customer</th>
                    <th className="py-1 pr-2">Display name</th>
                    <th className="py-1 pr-2">Tester</th>
                    <th className="py-1 pr-2">Period</th>
                    <th className="py-1 pr-2 text-right">Balance</th>
                    <th className="py-1 pr-2">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((r) => (
                    <tr
                      key={`${r.account_id}-${r.period}`}
                      className="border-t border-slate-100"
                    >
                      <td className="py-1 pr-2 font-mono">{r.frt_customer_code}</td>
                      <td className="py-1 pr-2 font-mono">{r.display_name}</td>
                      <td className="py-1 pr-2">{r.tester_name ?? '—'}</td>
                      <td className="py-1 pr-2 font-mono">{r.period}</td>
                      <td className="py-1 pr-2 text-right tabular-nums">
                        {formatBalance(r.balance_amount)}
                      </td>
                      <td className="py-1 pr-2 text-slate-500">{r.balance_source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
